import { ArrowLeft, Activity, Archive, BarChart3, Bell, Bookmark, Briefcase,
  Calendar, Circle, CreditCard, FileText, Globe, GraduationCap, Inbox, LayoutGrid, Lightbulb,
  Mail, Megaphone, MessageSquare, MessagesSquare, PoundSterling, Radio, Search,
  Settings, Share2, ShieldCheck, Sparkles, Star, Tag, UserRound, Users } from "lucide-react";

// Mobile-only "app menu" pattern (< sm breakpoint): a clean menu home (greeting,
// highlight stat, 3-col circular icon grid) and a sticky glass back header for opened
// sections. Desktop rendering is untouched, both components are sm:hidden.
const MENU_ICONS = {
  overview: LayoutGrid, listings: Briefcase, candidates: Users, messages: MessageSquare,
  interviews: Calendar, talent: Sparkles, settings: Settings,
  applications: FileText, saved: Bookmark, activity: Activity, alerts: Bell,
  profile: UserRound, ops: Activity, jobs: Briefcase, users: Users, verifications: ShieldCheck,
  payments: CreditCard, promos: Tag, pricing: PoundSterling, analytics: BarChart3,
  livetraffic: Radio, traffic: Globe, reviews: Star, marketing: Mail, insights: Lightbulb,
  indexing: Search, syndication: Share2, cvarchive: Archive, chats: MessagesSquare,
  campaigns: Megaphone, inbox: Inbox, findjobs: Search, courses: GraduationCap,
};

// soft hero-palette glows tucked into the corners, light, decorative, never under text
const CornerGlows = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
    <div className="absolute -top-20 -left-24 h-64 w-64 rounded-full bg-blue-200/50 blur-3xl" />
    <div className="absolute -top-16 -right-20 h-56 w-56 rounded-full bg-indigo-200/50 blur-3xl" />
    <div className="absolute top-1/2 -right-28 h-72 w-72 rounded-full bg-amber-200/40 blur-3xl" />
  </div>
);

const scrollTop = () => { try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (_) {} };

export function MobileMenuHome({ name, subtitle, stat, items, onSelect }) {
  return (
    <div className="sm:hidden relative px-5 pt-6 pb-14 max-w-md mx-auto min-h-[60vh]" data-testid="mobile-menu-home">
      <CornerGlows />
      <div className="relative">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-400">HiredToday</p>
        <h2 className="mt-1.5 font-display text-[26px] leading-8 tracking-tight font-bold text-zinc-950 truncate">{name}</h2>
        {subtitle && <p className="mt-1 text-sm text-zinc-500 truncate">{subtitle}</p>}

        {stat && (
          <div className="mt-7 rounded-2xl bg-gradient-to-br from-zinc-900 to-zinc-950 text-white p-5 shadow-md ring-1 ring-white/10"
            data-testid="mobile-menu-stat">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-400">{stat.label}</p>
            <p className="mt-1.5 font-display text-3xl font-bold tracking-tight">{stat.value}</p>
            {stat.hint && <p className="mt-1 text-xs text-zinc-400">{stat.hint}</p>}
          </div>
        )}

        <div className="grid grid-cols-3 gap-x-4 gap-y-8 pt-10 pb-6">
          {items.map((it) => {
            const Icon = MENU_ICONS[it.id] || Circle;
            return (
              <button key={it.id} type="button" data-testid={`mobile-menu-${it.id}`}
                onClick={() => { scrollTop(); onSelect(it.id); }}
                className="flex flex-col items-center active:scale-[0.92] transition-transform duration-200 ease-out">
                <span className="relative w-[68px] h-[68px] rounded-full bg-zinc-900 text-white flex items-center justify-center shadow-md ring-1 ring-white/10">
                  <Icon className="h-7 w-7" />
                  {it.badge > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center"
                      data-testid={`mobile-menu-badge-${it.id}`}>
                      {it.badge}
                    </span>
                  )}
                </span>
                <span className="text-xs font-medium text-zinc-600 mt-2.5 text-center leading-tight">{it.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function MobileBackHeader({ title, onBack }) {
  return (
    <div className="sm:hidden sticky top-0 z-40 flex items-center gap-2.5 h-14 px-1 bg-white/80 backdrop-blur-xl border-b border-zinc-200"
      data-testid="mobile-back-header">
      <button type="button" aria-label="Back to menu" data-testid="mobile-menu-back"
        onClick={() => { scrollTop(); onBack(); }}
        className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-zinc-200 bg-white/70 text-zinc-700 hover:bg-zinc-100 active:scale-95 transition-transform duration-150">
        <ArrowLeft className="h-4 w-4" />
      </button>
      <p className="text-sm font-semibold text-zinc-950">{title}</p>
    </div>
  );
}
